import { Groups } from "src/auth/types/userToken";
import { Action, canTakeAction, isUserMemberOfGroup } from "src/auth/utils/canTakeAction";
import NavPanelSection from "src/common/components/NavPanelSection";
import { useFeatureFlag } from "src/foundations/hooks/useFeatureFlag";
import TreeView from "src/ui/components/Tree";

export enum PaymentsNavPanelKeys {
  PAYMENT_POSTING = "payment_posting",
  PROVIDER_ADJUSTMENTS = "provider_adjustments",
  REFUNDS = "refunds",
  DME_BILLING = "dme_billing",
  VBC_BILLING = "vbc_billing",
  OCC_HEALTH_ORGANIZATIONS = "occ_health_organizations",
}

export function getDefaultWorkflow(): PaymentsNavPanelKeys {
  const isRcmPaymentsUser = isUserMemberOfGroup(Groups.RCM_PAYMENTS);
  const isRcmDmeBillingUser = isUserMemberOfGroup(Groups.RCM_DME_BILLING);
  const isRcmVbcBillingUser = isUserMemberOfGroup(Groups.RCM_VBC_BILLING);
  const hasOccHealthAccess = canTakeAction(Action.VIEW_OCC_HEALTH_ORGANIZATIONS);

  if (isRcmPaymentsUser) {
    return PaymentsNavPanelKeys.PAYMENT_POSTING;
  }

  if (isRcmDmeBillingUser) {
    return PaymentsNavPanelKeys.DME_BILLING;
  }

  if (isRcmVbcBillingUser) {
    return PaymentsNavPanelKeys.VBC_BILLING;
  }

  if (hasOccHealthAccess) {
    return PaymentsNavPanelKeys.OCC_HEALTH_ORGANIZATIONS;
  }

  // Fallback to payment posting if no specific access
  return PaymentsNavPanelKeys.PAYMENT_POSTING;
}

type PaymentsNavPanelContentsProps = {
  onWorkflowChange: (workflow?: PaymentsNavPanelKeys) => void;
  selectedWorkflow?: PaymentsNavPanelKeys;
};

function NavPanelPayments(props: PaymentsNavPanelContentsProps) {
  const { onWorkflowChange, selectedWorkflow } = props;

  const [isPaymentPostingRefundsVisible = false] = useFeatureFlag(
    "PAYMENT_POSTING_REFUNDS_VISIBLE",
    false
  );

  const hasOccHealthAccess = canTakeAction(Action.VIEW_OCC_HEALTH_ORGANIZATIONS);
  const isRcmPaymentsUser = isUserMemberOfGroup(Groups.RCM_PAYMENTS);
  const isRcmDmeBillingUser = isUserMemberOfGroup(Groups.RCM_DME_BILLING);
  const isRcmVbcBillingUser = isUserMemberOfGroup(Groups.RCM_VBC_BILLING);

  return (
    <>
      <NavPanelSection title="Workflows" iconName="bookmark" defaultExpanded={true}>
        <TreeView
          data={[
            ...(isRcmPaymentsUser
              ? [
                  { key: PaymentsNavPanelKeys.PAYMENT_POSTING, content: "Payment Posting" },
                  {
                    key: PaymentsNavPanelKeys.PROVIDER_ADJUSTMENTS,
                    content: "Provider Adjustments",
                  },
                ]
              : []),
            ...(isRcmPaymentsUser || isPaymentPostingRefundsVisible
              ? [
                  {
                    key: PaymentsNavPanelKeys.REFUNDS,
                    content: "Refunds",
                  },
                ]
              : []),
            ...(isRcmDmeBillingUser
              ? [{ key: PaymentsNavPanelKeys.DME_BILLING, content: "DME Billing" }]
              : []),
            ...(isRcmVbcBillingUser
              ? [{ key: PaymentsNavPanelKeys.VBC_BILLING, content: "VBC Billing" }]
              : []),
            ...(hasOccHealthAccess
              ? [
                  {
                    key: PaymentsNavPanelKeys.OCC_HEALTH_ORGANIZATIONS,
                    content: "Occ Health Organizations",
                  },
                ]
              : []),
          ]}
          accessibilityLabel="Workflows"
          selectedKey={selectedWorkflow ?? ""}
          onNodePress={({ key }) => onWorkflowChange(key)}
        />
      </NavPanelSection>
    </>
  );
}

export default NavPanelPayments;
